import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { AlertsService, AppAlert } from '../../../services/alerts.service';

type AlertsTab = 'all' | 'unread';

/**
 * The alerts dropdown, shared by both navs.
 *
 * Built on a CDK overlay rather than mat-menu: the panel is a rich surface with
 * tabs, a scroll region and a footer, and MatMenu projects everything into a
 * single wrapper and closes on any click that bubbles out of an item — both of
 * which this fights rather than uses.
 *
 * Owns the panel only. Each nav supplies its own trigger, marks it with
 * cdkOverlayOrigin and calls toggle(), since the two navs style their rows
 * completely differently.
 */
@Component({
  selector: 'app-alerts-menu',
  imports: [AsyncPipe, RouterLink, MatIconModule, CdkConnectedOverlay],
  templateUrl: './alerts-menu.html',
  styleUrl: './alerts-menu.css',
})
export class AlertsMenu {
  private alertsService = inject(AlertsService);

  /**
   * The nav's own trigger. Bound rather than passed to toggle() so it's set
   * during normal change detection — assigning it mid-click would create and
   * open the overlay in the same pass, which Angular flags.
   */
  @Input({ required: true }) origin!: CdkOverlayOrigin;

  isOpen = false;

  /**
   * Tried in order; the CDK takes the first that fits on screen. The student
   * trigger sits in a top bar so the first wins, the counselor trigger sits at
   * the bottom of the sidebar with no room below, so it falls through to the
   * side placement.
   */
  readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 8 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
  ];

  private tab = new BehaviorSubject<AlertsTab>('all');

  unreadCount$ = this.alertsService.unreadCount$;
  hasMore$ = this.alertsService.hasMore$;

  /** Whether there's anything to clear — independent of the active tab,
   *  since "Clear all" empties the whole list regardless of which one is
   *  showing. */
  hasDismissible$ = this.alertsService.alerts$.pipe(
    map((alerts) => alerts.some((a) => a.dismissible)),
  );

  /**
   * One stream for the whole panel. Persistent alerts ignore the tab filter —
   * they describe an outstanding action, not an event, so hiding them behind a
   * tab would defeat them.
   */
  view$ = combineLatest([this.alertsService.alerts$, this.tab]).pipe(
    map(([alerts, tab]) => {
      const persistent = alerts.filter((a) => a.notificationId === undefined);
      const feed = alerts.filter((a) => a.notificationId !== undefined);

      return {
        tab,
        persistent,
        items: tab === 'unread' ? feed.filter((a) => !a.read) : feed,
        emptyMessage: tab === 'unread' ? "You've read everything" : "You're all caught up",
      };
    }),
  );

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  selectTab(tab: AlertsTab): void {
    this.tab.next(tab);
  }

  markAllRead(): void {
    this.alertsService.markAllRead();
  }

  clearAll(): void {
    this.alertsService.dismissAll();
  }

  loadMore(): void {
    this.alertsService.loadMore();
  }

  /** Clicking an alert marks it read, navigates via routerLink, and closes. */
  onSelect(alert: AppAlert): void {
    this.alertsService.markRead(alert);
    this.close();
  }

  onDismiss(event: Event, alert: AppAlert): void {
    event.stopPropagation();
    event.preventDefault();
    this.alertsService.dismiss(alert);
  }

  /** Coarse relative age — precision stops mattering past a day. */
  age(date?: Date): string {
    if (!date) return '';

    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}hr ago`;

    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return date.toLocaleDateString(undefined, { weekday: 'long' });
    if (days < 14) return 'Last week';

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}
